class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      // On the cart page, don't open the drawer — navigate to /cart normally
      if (window.location.pathname === '/cart') return;
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        if (window.location.pathname === '/cart') return;
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Never open the drawer when already on the cart page
    if (window.location.pathname === '/cart') return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // Show exclusive panel only if not previously dismissed this session
      const exclusivePanel = this.querySelector('#CartDrawer-Exclusive');
      if (exclusivePanel) {
        try {
          const dismissed = sessionStorage.getItem('exclusive_panel_dismissed');
          exclusivePanel.classList.toggle('is-closed', dismissed === '1');
        } catch (e) {
          exclusivePanel.classList.remove('is-closed');
        }
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, open = true) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    try {
      const protectionId = getProtectionProductIdFromDrawer();
      const sourceCart =
        (parsedState && typeof parsedState.item_count === 'number' && parsedState) ||
        (window.cartJSON && typeof window.cartJSON === 'object' ? window.cartJSON : null);
      let isEmpty = isCartEffectivelyEmpty(sourceCart, protectionId);

      // If we just added an item (indicated by id, key, or items array in response), it's definitely not empty
      if (parsedState && (parsedState.id || parsedState.key || (Array.isArray(parsedState.items) && parsedState.items.length))) {
        isEmpty = false;
      }

      this.classList.toggle('is-empty', isEmpty);
      const itemsEl = document.getElementById('CartDrawer-Items');
      if (itemsEl) {
        itemsEl.classList.toggle('is-empty', isEmpty);
      }
    } catch (e) {
      console.error('Failed to sync cart drawer empty state', e);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (open) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = selector ? doc.querySelector(selector) : doc.querySelector('.shopify-section');
    return node ? node.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer-Items',
      },
      {
        id: 'cart-drawer',
        selector: '#Drawer-Footer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

let cartDrawerLoadingCount = 0;

function setCartDrawerLoading(isLoading) {
  const drawer = document.querySelector('#CartDrawer');
  if (!drawer) return;
  if (isLoading) {
    cartDrawerLoadingCount += 1;
  } else {
    cartDrawerLoadingCount = Math.max(0, cartDrawerLoadingCount - 1);
  }

  if (cartDrawerLoadingCount > 0) {
    drawer.classList.add('cart-drawer--loading');
  } else {
    drawer.classList.remove('cart-drawer--loading');
  }
}

function renderCartDrawerFromSections(sections) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
  if (!sections || typeof sections !== 'object') return;
  cartDrawer.renderContents({ sections });
}

function handleCartApiResponse(json) {
  if (!json || typeof json !== 'object') return;
  // cart/change.js and cart/update.js return full cart JSON (item_count, items, etc).
  // cart/add.js can return only added item + sections, so don't treat it as a full cart.
  const looksLikeCart =
    typeof json.item_count === 'number' && Array.isArray(json.items);

  if (looksLikeCart) {
    if (typeof window.handleCartUpdated === 'function') {
      window.handleCartUpdated(json);
    } else {
      window.cartJSON = json;
    }
  }

  if (json.sections) renderCartDrawerFromSections(json.sections);
}

function formatMoney(cents, format) {
  try {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const moneyFormat = format || window.Shopify.money_format || '${{amount}}';
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
  } catch (e) {
    console.error('formatMoney error', e);
  }

  const value = (cents || 0) / 100;
  return value.toFixed(2);
}

function getProtectionProductIdFromDrawer() {
  try {
    const drawerEl = document.querySelector('cart-drawer');
    const raw = drawerEl?.dataset?.protectionProductId;
    const id = raw ? Number(raw) : 0;
    return Number.isNaN(id) ? 0 : id;
  } catch {
    return 0;
  }
}

function isCartEffectivelyEmpty(cart, protectionProductId) {
  if (!cart) return true;
  const items = Array.isArray(cart.items) ? cart.items : [];
  if (!items.length) return true;
  const visibleItems = items.filter(
    (item) =>
      (!protectionProductId || Number(item.product_id) !== protectionProductId) &&
      !(item.properties && item.properties._gift === 'true'),
  );
  return visibleItems.length === 0;
}

if (!window.handleCartUpdated) {
  window.handleCartUpdated = function handleCartUpdated(cart) {
    window.cartJSON = cart;
    const protectionId = getProtectionProductIdFromDrawer();
    const items = Array.isArray(cart?.items) ? cart.items : [];

    const effectiveCount = items.filter(
      (item) =>
        (!protectionId || Number(item.product_id) !== protectionId) && item.quantity > 0
    ).reduce((acc, item) => acc + item.quantity, 0);

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart, itemCount: effectiveCount },
      }),
    );

    try {
      const cartItemCount = document.querySelector('#CartDrawer-ItemCount');
      if (cartItemCount) {
        cartItemCount.textContent = effectiveCount;
      }

      const drawer = document.querySelector('cart-drawer');
      const itemsEl = document.getElementById('CartDrawer-Items');
      const isEmpty = isCartEffectivelyEmpty(cart, protectionId);

      if (protectionId && effectiveCount === 0) {
        drawer.classList.add('is-empty');
        return;
      }

      if (drawer) {
        drawer.classList.toggle('is-empty', isEmpty);
      }
      if (itemsEl) {
        itemsEl.classList.toggle('is-empty', isEmpty);
      }
    } catch (e) {
      console.error('Failed to update cart drawer header/empty count', e);
    }
  };
}

if (!window.refreshCartFromServer) {
  window.refreshCartFromServer = async function refreshCartFromServer() {
    const cart = await fetch('/cart.js', { method: 'GET' }).then((r) => r.json());
    if (typeof window.handleCartUpdated === 'function') {
      window.handleCartUpdated(cart);
    } else {
      window.cartJSON = cart;
    }
    return cart;
  };
}

class CartDrawerItems extends CartItems {
  constructor() {
    super();
  }

  render(json) {
    super.render(json);
    try {
      const protectionId = getProtectionProductIdFromDrawer();
      const isEmpty = isCartEffectivelyEmpty(json, protectionId);
      const drawer = document.querySelector('cart-drawer');
      const itemsEl = document.getElementById('CartDrawer-Items');
      if (drawer) {
        drawer.classList.toggle('is-empty', isEmpty);
      }
      if (itemsEl) {
        itemsEl.classList.toggle('is-empty', isEmpty);
      }
    } catch (e) {
      console.error('CartDrawerItems render empty-state sync failed', e);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-Items',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#Drawer-Footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  async updateQuantity(line, quantity, name) {
    setCartDrawerLoading(true);
    try {
      await super.updateQuantity(line, quantity, name);
    } finally {
      setCartDrawerLoading(false);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerGift extends HTMLElement {
  static _enforcing = false;

  connectedCallback() {
    this.button = this.querySelector('.cart-drawer-gift__button');
    this.variantSelect = this.querySelector('[data-gift-variant-select]');
    this.variantId = Number(this.dataset.variantId);
    this.productId = Number(this.dataset.productId);
    this.thresholdCents = Number(this.dataset.thresholdCents || '0');
    this.protectionProductId = Number(this.dataset.protectionProductId || '0');
    this.giftIndex = this.dataset.giftIndex || '1';
    this.onClick = this.onClick.bind(this);
    this.button?.addEventListener('click', this.onClick);
    if (this.variantSelect) {
      this.variantId = Number(this.variantSelect.value);
      this.variantSelect.addEventListener('change', () => {
        this.variantId = Number(this.variantSelect.value);
      });
    }

    this.onCartUpdated = (event) => {
      const cart = event?.detail?.cart;
      if (!cart || CartDrawerGift._enforcing) return;
      this.syncVisibility(cart);
    };
    document.addEventListener('cart:updated', this.onCartUpdated);

    if (window.cartJSON && typeof window.cartJSON === 'object') {
      this.syncVisibility(window.cartJSON);
    }
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    document.removeEventListener('cart:updated', this.onCartUpdated);
  }

  async onClick() {
    if (!this.variantId) return;
    // Prevent double-click / spam
    if (this.button?.disabled) return;

    const isCartPage = window.location.pathname === '/cart';
    const originalLabel = this.button?.innerHTML;

    try {
      // Disable button + show loading state
      if (this.button) {
        this.button.disabled = true;
        this.button.classList.add('is-loading');
        this.button.innerHTML = `
          <span class="cart-drawer-gift__btn-spinner" aria-hidden="true"></span>
          <span class="visually-hidden">Adding…</span>
        `;
      }
      setCartDrawerLoading(true);

      const addBody = {
        items: [{ id: this.variantId, quantity: 1, properties: { _gift: 'true', _gift_index: String(this.giftIndex) } }],
      };

      if (isCartPage) {
        // 1. Add the gift
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(addBody),
        });

        // 2. Re-render main-cart sections the same way CartItems.updateQuantity does —
        //    POST to cart/update.js with empty updates to get fresh rendered HTML
        const cartItemsEl = document.querySelector('cart-items');
        const sectionsToFetch = cartItemsEl
          ? cartItemsEl.getSectionsToRender().map((s) => s.section)
          : ['cart-icon-bubble'];

        const json = await fetch(routes.cart_update_url, {
          ...window.fetchConfig(),
          body: JSON.stringify({
            updates: {},
            sections: sectionsToFetch,
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());

        // 3. Let CartItems handle DOM updates + dispatch cart:updated
        if (cartItemsEl) {
          cartItemsEl.render(json);
        } else if (typeof window.handleCartUpdated === 'function') {
          window.handleCartUpdated(json);
        }
        return;
      }

      // Other pages — add gift and open/refresh cart drawer
      const json = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...addBody,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());
      handleCartApiResponse(json);
      if (typeof window.refreshCartFromServer === 'function') {
        await window.refreshCartFromServer();
      }
    } catch (e) {
      console.error('Failed to add free gift', e);
      // Restore button on error so user can retry
      if (this.button && originalLabel !== undefined) {
        this.button.innerHTML = originalLabel;
        this.button.disabled = false;
        this.button.classList.remove('is-loading');
      }
    } finally {
      setCartDrawerLoading(false);
    }
  }

  syncVisibility(cart) {
    if (!this.thresholdCents || !this.productId) {
      this.hidden = true;
      CartDrawerGift.updateContainerVisibility();
      return;
    }

    const items = Array.isArray(cart?.items) ? cart.items : [];
    const total = CartDrawerGift.calcEffectiveTotal(cart, this.protectionProductId);

    const hasGift = items.some(
      (item) =>
        Number(item.product_id) === this.productId &&
        item.properties &&
        (String(item.properties._gift_index) === String(this.giftIndex) ||
         item.properties._Special === 'Free Gift Promotion'),
    );

    const thresholdCents =
      Shopify.currency.rate === 1
        ? this.thresholdCents
        : Math.round(this.thresholdCents * Shopify.currency.rate * (1 + 0.015));

    const thresholdOk = total >= thresholdCents;
    this.hidden = !(thresholdOk && !hasGift);
    CartDrawerGift.updateContainerVisibility();
  }

  static updateContainerVisibility() {
    // Update both cart drawer and main cart gift containers
    document.querySelectorAll('.js-cart-drawer-gifts-container, .js-main-cart-gifts-container').forEach((container) => {
      const anyVisible = container.querySelector('cart-drawer-gift:not([hidden])');
      container.hidden = !anyVisible;
    });
  }

  static calcEffectiveTotal(cart, protectionProductId) {
    if (!cart) return 0;
    let total = typeof cart.total_price === 'number' ? cart.total_price : 0;
    const items = Array.isArray(cart.items) ? cart.items : [];
    items.forEach((item) => {
      if (item.gift_card) total -= item.final_line_price || 0;
      if (protectionProductId && Number(item.product_id) === protectionProductId)
        total -= item.final_line_price || 0;
      if (item.properties && item.properties._gift === 'true')
        total -= item.final_line_price || 0;
    });
    return total;
  }

  /**
   * - Remove gifts whose threshold is no longer met
   * - Cap quantity to 1 for gifts that have qty > 1
   */
  static async enforceAllGifts(cart) {
    if (CartDrawerGift._enforcing || !cart) return;
    const items = Array.isArray(cart.items) ? cart.items : [];
    const giftEls = document.querySelectorAll('cart-drawer-gift');
    if (!giftEls.length) return;

    const protectionProductId = giftEls[0]?.protectionProductId || 0;
    const total = CartDrawerGift.calcEffectiveTotal(cart, protectionProductId);

    const updates = {};

    giftEls.forEach((el) => {
      const giftIndex = el.giftIndex || el.dataset?.giftIndex;
      const productId = el.productId || Number(el.dataset?.productId);
      const thresholdCents = el.thresholdCents || Number(el.dataset?.thresholdCents || 0);

      const convertedThreshold =
        Shopify.currency.rate === 1
          ? thresholdCents
          : Math.round(thresholdCents * Shopify.currency.rate * (1 + 0.015));

      const thresholdOk = total >= convertedThreshold;

      const giftItem = items.find(
        (item) =>
          Number(item.product_id) === productId &&
          item.properties &&
          (String(item.properties._gift_index) === String(giftIndex) ||
           item.properties._Special === 'Free Gift Promotion'),
      );

      if (!giftItem || !giftItem.key) return;

      if (!thresholdOk) {
        updates[giftItem.key] = 0;
      } else if (Number(giftItem.quantity) > 1) {
        updates[giftItem.key] = 1;
      }
    });
    if (!Object.keys(updates).length) return;

    CartDrawerGift._enforcing = true;
    try {
      setCartDrawerLoading(true);
      const json = await fetch(routes.cart_update_url, {
        ...window.fetchConfig(),
        body: JSON.stringify({
          updates,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());
      handleCartApiResponse(json);
    } catch (e) {
      console.error('Failed to batch-update gift lines', e);
    } finally {
      CartDrawerGift._enforcing = false;
      setCartDrawerLoading(false);
    }
  }

  static async refreshDrawerSections() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
    const data = await fetch('/?sections=cart-drawer,cart-icon-bubble', { method: 'GET' }).then((r) => r.json());
    cartDrawer.renderContents({ sections: data });
  }
}

customElements.define('cart-drawer-gift', CartDrawerGift);

class CartDrawerProgress extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;

    const data = window.cart_progress_data;
    if (!data) {
      setTimeout(() => this.connectedCallback(), 50);
      return;
    }

    this._initialized = true;

    if (!data.enabled || !Array.isArray(data.thresholds) || !data.thresholds.length) {
      this.style.display = 'none';
      return;
    }

    this.protectionProductId = Number(data.protectionProductId || 0);
    this.finalMessage = data.finalMessage || '';
    this.rtdMessage = data.rtdMessage || '';
    this.thresholds = data.thresholds
      .filter((t) => t.threshold > 0)
      .map((t) => ({
        ...t,
        threshold: this.convertToBaseCurrency(t.threshold),
        thresholdCents: Math.round(this.convertToBaseCurrency(t.threshold) * 100),
      }))
      .sort((a, b) => a.threshold - b.threshold);

    if (!this.thresholds.length) {
      this.style.display = 'none';
      return;
    }

    this.thresholdsAll = this.thresholds;
    this._hasRtd = null;

    this.messageEl = this.querySelector('.cart-drawer-progress__message');
    this.fillEl = this.querySelector('.cart-drawer-progress__fill');

    const barEl = this.querySelector('.cart-drawer-progress__bar');
    this.barEl = barEl;

    const readIcon = (attr) => {
      const el = document.querySelector(`[${attr}]`);
      if (!el) return '';
      if (el instanceof HTMLTemplateElement) {
        // DocumentFragment has no .innerHTML — clone into a div to serialize
        const tmp = document.createElement('div');
        tmp.appendChild(el.content.cloneNode(true));
        return tmp.innerHTML;
      }
      return el.innerHTML || '';
    };
    this.truckIcon = readIcon('data-cart-progress-truck-icon');
    this.giftIcon = readIcon('data-cart-progress-gift-icon');

    this.syncThresholdsForRtd();

    this.onCartUpdated = (event) => {
      const cart = event?.detail?.cart || window.cartJSON;
      const runner = () => this.renderFromCart(cart);
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(runner);
      } else {
        setTimeout(runner, 0);
      }
    };

    document.addEventListener('cart:updated', this.onCartUpdated);

    if (window.cartJSON && typeof window.cartJSON === 'object') {
      this.renderFromCart(window.cartJSON);
    } else {
      fetch('/cart.js')
        .then((r) => r.json())
        .then((json) => {
          window.cartJSON = json;
          this.renderFromCart(json);
        })
        .catch(console.error);
    }
  }

  getHasRtd() {
    try {
      return Boolean(document.querySelector('#CartDrawer-Items [data-cart-has-rtd="true"]'));
    } catch (e) {
      return false;
    }
  }

  syncThresholdsForRtd() {
    const hasRtd = this.getHasRtd();
    if (this._hasRtd === hasRtd && Array.isArray(this.thresholds) && this.maxThreshold) return;
    this._hasRtd = hasRtd;

    // Hide the "shipping" milestone (the one without a productId) when RTD exists.
    this.thresholds = hasRtd
      ? this.thresholdsAll.filter((t) => t.productId)
      : this.thresholdsAll;

    if (!this.thresholds.length) {
      this.style.display = 'none';
      return;
    }

    this.style.display = '';
    this.maxThreshold = Math.max(...this.thresholds.map((t) => t.threshold));
    this.buildMarkers();
  }

  buildMarkers() {
    if (!this.barEl) return;
    this.barEl.querySelectorAll('.cart-drawer-progress__marker').forEach((el) => el.remove());

    this.thresholds.forEach((t) => {
      const marker = document.createElement('div');
      marker.className = 'cart-drawer-progress__marker';
      marker.dataset.progressMarkerIndex = t.index;

      const pct = (t.threshold / this.maxThreshold) * 100;
      if (pct >= 100) {
        marker.style.right = '-6px';
      } else {
        marker.style.left = `calc(${pct}% - 31px)`;
      }

      const amountDiv = document.createElement('div');
      amountDiv.className = 'cart-drawer-progress__marker-amount';
      amountDiv.dataset.progressMarkerIndex = t.index;
      amountDiv.innerHTML = formatMoney(t.thresholdCents);

      const iconDiv = document.createElement('div');
      iconDiv.className = 'cart-drawer-progress__marker-icon';
      iconDiv.innerHTML = t.productId ? this.giftIcon : this.truckIcon;

      const labelDiv = document.createElement('div');
      labelDiv.className = 'cart-drawer-progress__marker-label';
      labelDiv.textContent = t.title || '';

      marker.appendChild(amountDiv);
      marker.appendChild(iconDiv);
      marker.appendChild(labelDiv);
      this.barEl.appendChild(marker);
    });

    this.markerEls = this.querySelectorAll('.cart-drawer-progress__marker');
  }

  convertToBaseCurrency(amount) {
    const rate = Shopify.currency.rate;
    return rate == 1 ? amount : amount * rate * (1 + 0.015);
  }

  disconnectedCallback() {
    if (this.onCartUpdated) {
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }
  }

  renderFromCart(cart) {
    if (!cart || !this.thresholds.length) return;
    this.syncThresholdsForRtd();
    if (!this.thresholds.length) return;

    const items = Array.isArray(cart.items) ? cart.items : [];
    let totalGiftCards = 0;
    let totalProtection = 0;
    let totalGifts = 0;

    items.forEach((item) => {
      if (item.gift_card) {
        totalGiftCards += item.final_line_price || 0;
      }
      if (this.protectionProductId && Number(item.product_id) === this.protectionProductId) {
        totalProtection += item.final_line_price || 0;
      }
      if (item.properties && item.properties._gift === 'true') {
        totalGifts += item.final_line_price || 0;
      }
    });

    const cartTotalWithoutExtras = (cart.total_price || 0) - totalGiftCards - totalProtection - totalGifts;
    const cartValue = cartTotalWithoutExtras / 100;

    let progressPercent = (cartValue / this.maxThreshold) * 100;
    progressPercent = Math.max(0, Math.min(100, progressPercent));

    if (this.fillEl) {
      this.fillEl.style.width = `${progressPercent}%`;
    }

    this.markerEls.forEach((markerEl) => {
      const idx = Number(markerEl.dataset.progressMarkerIndex);
      const t = this.thresholds.find((th) => th.index === idx);
      if (!t) return;
      const isComplete = cartValue >= t.threshold;
      markerEl.classList.toggle('cart-drawer-progress__marker--active', isComplete);
    });

    const message = this.computeMessage(cartValue, cartTotalWithoutExtras);
    if (this.messageEl) {
      this.messageEl.innerHTML = message || '';
      this.messageEl.style.display = message ? '' : 'none';
    }

    if (typeof CartDrawerGift !== 'undefined') {
      CartDrawerGift.enforceAllGifts(cart);
    }
  }

  computeMessage(cartValue, cartTotalCents) {
    const hasRtd = Boolean(this._hasRtd);
    for (const t of this.thresholds) {
      if (cartValue < t.threshold) {
        const remainingCents = Math.max(0, t.thresholdCents - cartTotalCents);
        const remainingMoney = formatMoney(remainingCents);
        const msg = hasRtd && t.rtdProgressMessage ? t.rtdProgressMessage : t.progressMessage;
        return String(msg || '').replace('[amount]', remainingMoney);
      }
    }

    if (hasRtd && this.rtdMessage) return this.rtdMessage;
    return this.finalMessage;
  }
}

customElements.define('cart-drawer-progress', CartDrawerProgress);

/**
 * Clone cart-drawer-gift elements from the global template into the main cart
 * page container so gifts appear on both the drawer and the /cart page.
 */
function initMainCartGifts(force = false) {
  const template = document.getElementById('cart-drawer-gifts-global-template');
  const container = document.querySelector('.js-main-cart-gifts-container');
  if (!template || !container) return;
  // Skip if already populated — unless forced (e.g. after section re-render wipes DOM)
  if (!force && container.children.length > 0) return;

  // Clear any stale clones before re-populating
  container.innerHTML = '';

  const content = template.content.cloneNode(true);
  const gifts = content.querySelectorAll('cart-drawer-gift');
  if (!gifts.length) return;

  // Give cloned gifts unique IDs to avoid DOM conflicts with drawer copies
  gifts.forEach((gift) => {
    const origId = gift.id;
    if (origId) gift.id = origId + '-main-cart';
  });

  container.appendChild(content);
  container.hidden = false;

  // Let the container visibility be managed by the same static method
  CartDrawerGift.updateContainerVisibility();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMainCartGifts);
} else {
  initMainCartGifts();
}

class CartDrawerDiscount extends HTMLElement {
  static storageKey = 'cart_drawer_discount_code';

  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="discount"]');
    this.status = this.querySelector('.cart-drawer-discount__status');
    this.meta = this.querySelector('.cart-drawer-discount__meta');
    this.codeEl = this.querySelector('[data-discount-code]');
    this.removeBtn = this.querySelector('[data-discount-remove]');
    this.form?.addEventListener('submit', this.onSubmit);
    this.removeBtn?.addEventListener('click', this.onRemove);

    // this.restore();
    this.bindCheckoutRedirect();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.removeBtn?.removeEventListener('click', this.onRemove);
    document.removeEventListener('click', this.onCheckoutClick, true);
  }

  restore() {
    const domCode = (this.codeEl?.textContent || '').trim();
    if (domCode) {
      window.localStorage.setItem(CartDrawerDiscount.storageKey, domCode);
      if (this.input) this.input.value = domCode;
      this.setAppliedUI(domCode);
      return;
    }

    const cartCode = window.cartJSON?.discount_codes?.[0]?.code?.trim?.() || '';
    if (cartCode) {
      window.localStorage.setItem(CartDrawerDiscount.storageKey, cartCode);
      if (this.input) this.input.value = cartCode;
      this.setAppliedUI(cartCode);
      return;
    }

    const code = (window.localStorage.getItem(CartDrawerDiscount.storageKey) || '').trim();
    if (!code) return;
    if (this.input) this.input.value = code;
    this.setAppliedUI(code);
  }

  setStatus(message = '') {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.classList.remove('cart-drawer-discount__status--error');
  }

  setAppliedUI(code) {
    if (this.codeEl) this.codeEl.textContent = code;
    if (this.meta) this.meta.hidden = false;
    this.setStatus('');
  }

  clearAppliedUI() {
    if (this.codeEl) this.codeEl.textContent = '';
    if (this.meta) this.meta.hidden = true;
    this.setStatus('');
  }

  onRemove = async () => {
    try {
      setCartDrawerLoading(true);
      this.setStatus('Removing…');

      // Shopify Ajax Cart API supports removing all cart discounts via discount:''.
      // (Not a dedicated "remove one specific code" endpoint.)
      const resp = await fetch(`${routes.cart_update_url}`, {
        ...window.fetchConfig(),
        ...{
          body: JSON.stringify({
            discount: '',
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        },
      });

      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json?.status) {
        throw new Error(json?.description || 'cart-update-failed');
      }

      // Prefer section patch returned from /cart/update.js; otherwise fallback to full drawer refresh.
      handleCartApiResponse(json);
      if (!json?.sections) {
        await this.refreshDrawerSections();
      }

      window.localStorage.removeItem(CartDrawerDiscount.storageKey);
      this.clearAppliedUI();
    } catch (e) {
      console.error('CartDrawerDiscount remove error', e);
      // Keep UI untouched on failure.
      this.setStatus('');
    } finally {
      setCartDrawerLoading(false);
    }
  };

  onSubmit = async (event) => {
    event.preventDefault();
    console.log('onSubmit', event);

    const code = (this.input?.value || '').trim();
    if (!code) return;

    this.setStatus('Applying…');

    try {
      const resp = await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET' });
      if (!resp.ok) throw new Error('discount-endpoint-failed');

      const cart = await window.refreshCartFromServer();
      const applicable =
        Array.isArray(cart?.discount_codes) &&
        cart.discount_codes.length > 0 &&
        cart.discount_codes[0]?.applicable;

      if (!applicable) throw new Error('discount-not-applicable');

      window.localStorage.setItem(CartDrawerDiscount.storageKey, code);
      this.setAppliedUI(code);
      this.setStatus('Applied');

      await this.refreshDrawerSections();
    } catch (e) {
      window.localStorage.removeItem(CartDrawerDiscount.storageKey);
      this.clearAppliedUI();
      this.setStatus('Invalid discount code');
      if (this.status) this.status.classList.add('cart-drawer-discount__status--error');
    }
  };

  async refreshDrawerSections() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;

    const data = await fetch('/?sections=cart-drawer,cart-icon-bubble', { method: 'GET' }).then((r) => r.json());
    cartDrawer.renderContents({ sections: data });
  }

  bindCheckoutRedirect() {
    document.addEventListener('click', this.onCheckoutClick, true);
  }

  onCheckoutClick = (event) => {
    const btn = event.target?.closest?.('#CartDrawer-Checkout');
    if (!btn) return;

    const code = (window.localStorage.getItem(CartDrawerDiscount.storageKey) || '').trim();
    if (!code) return;

    event.preventDefault();
    event.stopPropagation();
    window.location.href = `/checkout?discount=${encodeURIComponent(code)}`;
  };
}

customElements.define('cart-drawer-discount', CartDrawerDiscount);

class CartDrawerProtection extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    this.variantId = this.dataset.variantId;
    this.productId = Number(this.dataset.productId);
    this.onChange = this.onChange.bind(this);
    this.input?.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('change', this.onChange);
  }

  async onChange() {
    if (!this.variantId) return;
    const isCartPage = window.location.pathname === '/cart';

    // Prevent spam during request
    if (this.input) this.input.disabled = true;

    try {
      setCartDrawerLoading(true);

      // Helper: refresh main cart sections like CartItems does
      const refreshMainCart = async () => {
        const cartItemsEl = document.querySelector('cart-items');
        if (!cartItemsEl) return;
        const sectionsToFetch = cartItemsEl.getSectionsToRender().map((s) => s.section);
        const json = await fetch(routes.cart_update_url, {
          ...window.fetchConfig(),
          body: JSON.stringify({
            updates: {},
            sections: sectionsToFetch,
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());
        cartItemsEl.render(json);
      };

      if (this.input.checked) {
        // Add protection product
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(this.variantId), quantity: 1, properties: {} }],
            ...(!isCartPage && {
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          }),
        }).then((r) => r.json()).then((json) => {
          if (!isCartPage) handleCartApiResponse(json);
        });

        if (isCartPage) {
          await refreshMainCart();
        } else if (typeof window.refreshCartFromServer === 'function') {
          await window.refreshCartFromServer();
        }
      } else {
        // Remove protection product
        const cartBefore = window.cartJSON && typeof window.cartJSON === 'object'
          ? window.cartJSON
          : await window.refreshCartFromServer();

        const idx = Array.isArray(cartBefore?.items)
          ? cartBefore.items.findIndex((it) => Number(it.product_id) === this.productId)
          : -1;

        if (idx > -1) {
          const line = idx + 1;
          if (isCartPage) {
            // Remove via cart_change then re-render sections
            await fetch(routes.cart_change_url, {
              ...window.fetchConfig(),
              body: JSON.stringify({ line, quantity: 0 }),
            });
            await refreshMainCart();
          } else {
            const json = await fetch(routes.cart_change_url, {
              ...window.fetchConfig(),
              body: JSON.stringify({
                line,
                quantity: 0,
                sections: ['cart-drawer', 'cart-icon-bubble'],
                sections_url: window.location.pathname,
              }),
            }).then((r) => r.json());
            handleCartApiResponse(json);
          }
        }
      }
    } catch (e) {
      // Revert toggle on error
      if (this.input) this.input.checked = !this.input.checked;
    } finally {
      // Re-enable checkbox
      if (this.input) this.input.disabled = false;
      // Sync all other protection instances (cart drawer & main cart)
      const checked = this.input?.checked;
      document.querySelectorAll('cart-drawer-protection').forEach((el) => {
        if (el !== this && el.querySelector('input[type="checkbox"]')) {
          el.querySelector('input[type="checkbox"]').checked = checked;
        }
      });
      setCartDrawerLoading(false);
    }
  }

  async refreshDrawerSections() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
    const data = await fetch('/?sections=cart-drawer,cart-icon-bubble', { method: 'GET' }).then((r) => r.json());
    cartDrawer.renderContents({ sections: data });
  }
}

customElements.define('cart-drawer-protection', CartDrawerProtection);


class CartDrawerSubscription extends HTMLElement {
  connectedCallback() {
    this.lineKey = this.dataset.lineKey;
    this.quantity = Number(this.dataset.quantity || '1');
    this.checkbox = this.querySelector('.cart-drawer-subscription__checkbox');
    this.select = this.querySelector('.cart-drawer-subscription__select');
    this.selectWrapper = this.querySelector('.cart-drawer-subscription__select-wrapper');

    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.onToggle);
    }
    if (this.select) {
      this.select.addEventListener('change', this.onChangeSelect);
    }
  }

  disconnectedCallback() {
    if (this.checkbox) {
      this.checkbox.removeEventListener('change', this.onToggle);
    }
    if (this.select) {
      this.select.removeEventListener('change', this.onChangeSelect);
    }
  }

  onToggle = async () => {
    const enabled = this.checkbox.checked;
    if (!enabled) {
      if (this.selectWrapper) {
        this.selectWrapper.classList.add('cart-drawer-subscription__select-wrapper--hidden');
      }
      await this.updateSubscription(null);
      return;
    }

    const sellingPlanId = this.select ? Number(this.select.value || '0') : 0;
    if (!sellingPlanId) return;
    await this.updateSubscription(sellingPlanId);
  };

  onChangeSelect = async () => {
    if (!this.checkbox || !this.checkbox.checked) return;
    const sellingPlanId = Number(this.select.value || '0');
    if (!sellingPlanId) return;
    await this.updateSubscription(sellingPlanId);
  };

  async updateSubscription(sellingPlanId) {
    if (!this.lineKey) return;

    try {
      setCartDrawerLoading(true);

      const payload = {
        id: this.lineKey,
        quantity: this.quantity,
      };

      if (sellingPlanId) {
        payload.selling_plan = sellingPlanId;
      } else {
        payload.selling_plan = null;
      }

      const body = JSON.stringify({
        ...payload,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      });

      const json = await fetch(`${routes.cart_change_url}`, {
        ...window.fetchConfig(),
        ...{ body },
      }).then((r) => r.json());
      handleCartApiResponse(json);
    } catch (e) {
      console.error('Failed to update subscription in cart drawer', e);
    } finally {
      setCartDrawerLoading(false);
    }
  }
}

customElements.define('cart-drawer-subscription', CartDrawerSubscription);

class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || 'cart-drawer-recommendation';
    this.limit = Number(this.dataset.limit);
    this.root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.initialized = false;
    this._swiper = null;
    this._loading = false;
    this._lastRequestKey = '';
    this._abortController = null;

    this.load = this.load.bind(this);
    document.addEventListener('cart:updated', this.load);
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.load);
    try {
      this._abortController?.abort?.();
    } catch (e) { }
  }

  async load() {
    if (this._loading) return;
    if (this.initialized && !window.cartJSON) {
      return;
    }
    try {
      const cart =
        (window.cartJSON && typeof window.cartJSON === 'object'
          ? window.cartJSON
          : null) || (await window.refreshCartFromServer?.());

      if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
        this.style.display = 'none';
        return;
      }

      const baseItem = cart.items.find((item) => {
        if (item.gift_card) return false;
        if (item.properties && item.properties._gift === 'true') return false;
        return true;
      });

      if (!baseItem || !baseItem.product_id) {
        this.style.display = 'none';
        return;
      }

      const requestKey = `${String(baseItem.product_id)}|${String(cart?.token || '')}|${String(this.limit)}|${String(this.sectionId)}`;
      if (requestKey && requestKey === this._lastRequestKey) return;
      this._lastRequestKey = requestKey;

      const url =
        (window.routes && window.routes.product_recommendations_url
          ? window.routes.product_recommendations_url
          : this.root + 'recommendations/products') +
        '?section_id=' +
        encodeURIComponent(this.sectionId) +
        '&product_id=' +
        encodeURIComponent(baseItem.product_id) +
        '&limit=' +
        encodeURIComponent(this.limit)

      this._loading = true;
      try {
        try {
          this._abortController?.abort?.();
        } catch (e) { }
        this._abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

        const response = await fetch(url, {
          method: 'GET',
          signal: this._abortController ? this._abortController.signal : undefined,
        });
        const html = await response.text();

        if (!html.trim()) {
          this.style.display = 'none';
          return;
        }

        this.innerHTML = html;
        this.initialized = true;
        this.style.display = '';

        const sectionRoot = this.querySelector('.cart-drawer-upsell');
        if (!sectionRoot) {
          this.style.display = 'none';
          return;
        }

        try {
          const Swiper = window.Swiper;
          const swiperEl = this.querySelector('[data-upsell-swiper]');
          if (Swiper && swiperEl) {
            const nextEl = this.querySelector('[data-upsell-next]');
            const prevEl = this.querySelector('[data-upsell-prev]');
            const scrollbarEl = this.querySelector('[data-upsell-scrollbar]');
            if (this._swiper && typeof this._swiper.destroy === 'function') {
              this._swiper.destroy(true, true);
            }
            this._swiper = new Swiper(swiperEl, {
              slidesPerView: 'auto',
              spaceBetween: 16,
              freeMode: true,
              watchOverflow: true,
              navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
              scrollbar: scrollbarEl ? { el: scrollbarEl, draggable: true } : undefined,
            });
          }
        } catch (e) {
          console.error('Failed to init Swiper for upsell', e);
        }

        sectionRoot.addEventListener('change', (event) => {
          const selectEl = event.target.closest('[data-upsell-variant-select]');
          if (!selectEl) return;

          const itemEl = selectEl.closest('.cart-drawer-upsell-item');
          const errEl = itemEl?.querySelector('[data-upsell-variant-error]');
          if (errEl) {
            errEl.hidden = true;
            errEl.textContent = '';
          }
          selectEl.removeAttribute('aria-invalid');

          const selectedVariantId = Number(selectEl.value || '0');
          if (!selectedVariantId) return;

          let variants = [];
          try {
            if (!selectEl._upsellVariantsCache) {
              const raw = selectEl.getAttribute('data-variants') || '[]';
              selectEl._upsellVariantsCache = JSON.parse(raw);
            }
            variants = Array.isArray(selectEl._upsellVariantsCache)
              ? selectEl._upsellVariantsCache
              : [];
          } catch (e) {
            variants = [];
          }

          const variant = variants.find((v) => Number(v?.id) === selectedVariantId);
          if (!variant) return;

          const priceCents = Number(variant.price || 0);
          const compareAtCents = Number(variant.compare_at_price || 0);
          const price = formatMoney(priceCents);
          const compareAt =
            compareAtCents > priceCents ? formatMoney(compareAtCents) : '';
          const discountBadge =
            compareAtCents > priceCents
              ? `SAVE ${Math.round(((compareAtCents - priceCents) * 100) / compareAtCents)}%`
              : '';

          if (!itemEl) return;

          const priceRow = itemEl.querySelector('.cart-drawer-upsell-item__price-row');
          if (!priceRow) return;

          if (compareAt && compareAt !== '') {
            priceRow.innerHTML = `
            <span class="cart-drawer-upsell-item__price-original">${compareAt}</span>
            <span class="cart-drawer-upsell-item__price-current">${price}</span>
            <span class="cart-drawer-upsell-item__badge">${discountBadge}</span>
          `;
          } else {
            priceRow.innerHTML = `
            <span class="cart-drawer-upsell-item__price-current">${price}</span>
          `;
          }
        });

        sectionRoot.addEventListener('click', async (event) => {
          const btn = event.target.closest('[data-upsell-add]');
          if (!btn) return;
          const itemEl = btn.closest('.cart-drawer-upsell-item');
          if (!itemEl) return;
          const selectEl = itemEl.querySelector('[data-upsell-variant-select]');
          if (selectEl) {
            const raw = (selectEl.value || '').trim();
            if (!raw) {
              const errEl = itemEl.querySelector('[data-upsell-variant-error]');
              const msg =
                (itemEl.dataset.upsellChooseSizeError || '').trim() || 'Choose a size.';
              if (errEl) {
                errEl.textContent = msg;
                errEl.hidden = false;
              }
              selectEl.setAttribute('aria-invalid', 'true');
              selectEl.focus();
              return;
            }
            selectEl.removeAttribute('aria-invalid');
            const errClear = itemEl.querySelector('[data-upsell-variant-error]');
            if (errClear) {
              errClear.hidden = true;
              errClear.textContent = '';
            }
          }
          const variantId = Number(selectEl ? selectEl.value : itemEl.dataset.variantId || '0');
          if (!variantId) return;

          try {
            setCartDrawerLoading(true);
            btn.disabled = true;
            const json = await fetch(this.root + 'cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
              },
              body: JSON.stringify({
                items: [{ id: variantId, quantity: 1 }],
                sections: ['cart-drawer', 'cart-icon-bubble'],
                sections_url: window.location.pathname,
              }),
            }).then((r) => r.json());

            // cart/add.js might not return full cart JSON; render sections now and refresh cart state once.
            handleCartApiResponse(json);

            if (typeof window.refreshCartFromServer === 'function') {
              await window.refreshCartFromServer();
            }
          } catch (e) {
            console.error('Failed to add upsell item', e);
          } finally {
            btn.disabled = false;
            setCartDrawerLoading(false);
          }
        });
      } finally {
        this._loading = false;
      }
    } catch (e) {
      this._loading = false;
      console.error('CartDrawerUpsell error', e);
      this.style.display = 'none';
    }
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);

/* ========== Exclusive Cart Panel ========== */
(function initExclusivePanel() {
  const panel = document.getElementById('CartDrawer-Exclusive');
  if (!panel) return;

  // Close button
  const closeBtn = panel.querySelector('[data-exclusive-close]');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      panel.classList.add('is-closed');
      try { sessionStorage.setItem('exclusive_panel_dismissed', '1'); } catch (e) { }
    });
  }

  // Variant select → update price + button variant id
  panel.querySelectorAll('[data-exclusive-variant-select]').forEach((select) => {
    const item = select.closest('.cart-drawer-exclusive-item');
    if (!item) return;
    const addBtn = item.querySelector('[data-exclusive-add]');
    const priceRow = item.querySelector('.cart-drawer-exclusive-item__price-row');

    select.addEventListener('change', () => {
      const option = select.options[select.selectedIndex];
      if (!option || !option.value) return;

      // Clear error state
      select.classList.remove('cart-drawer-exclusive-item__select--error');
      const err = item.querySelector('.cart-drawer-exclusive-item__variant-error');
      if (err) err.hidden = true;

      if (addBtn) addBtn.dataset.variantId = option.value;

      // Update price display
      if (priceRow) {
        const price = Number(option.dataset.price || 0);
        const compareAt = Number(option.dataset.compareAt || 0);

        if (compareAt > price && compareAt > 0) {
          const discount = Math.round(((compareAt - price) / compareAt) * 100);
          priceRow.innerHTML = `
            <span class="cart-drawer-exclusive-item__price-current">${formatMoney(price)}</span>
            <span class="cart-drawer-exclusive-item__price-original">${formatMoney(compareAt)}</span>
            <span class="cart-drawer-exclusive-item__badge">SAVE ${discount}%</span>
          `;
        } else {
          priceRow.innerHTML = `
            <span class="cart-drawer-exclusive-item__price-current">${formatMoney(price)}</span>
          `;
        }
      }
    });
  });

  // Add to cart buttons
  panel.querySelectorAll('[data-exclusive-add]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const item = btn.closest('.cart-drawer-exclusive-item');
      const select = item ? item.querySelector('[data-exclusive-variant-select]') : null;

      // Validate: if select exists but nothing chosen
      if (select && !select.value) {
        select.classList.add('cart-drawer-exclusive-item__select--error');
        let errorEl = item.querySelector('.cart-drawer-exclusive-item__variant-error');
        if (!errorEl) {
          errorEl = document.createElement('p');
          errorEl.className = 'cart-drawer-exclusive-item__variant-error';
          select.insertAdjacentElement('afterend', errorEl);
        }
        errorEl.textContent = 'Please select an option.';
        errorEl.hidden = false;
        setTimeout(() => {
          select.classList.remove('cart-drawer-exclusive-item__select--error');
        }, 2000);
        return;
      }

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      // Clear any previous error
      if (select) {
        select.classList.remove('cart-drawer-exclusive-item__select--error');
        const err = item.querySelector('.cart-drawer-exclusive-item__variant-error');
        if (err) err.hidden = true;
      }

      const originalText = btn.textContent;
      btn.textContent = 'ADDING...';
      btn.disabled = true;

      try {
        setCartDrawerLoading(true);

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        }).then((r) => r.json());

        await window.refreshCartFromServer();

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          const data = await fetch('/?sections=cart-drawer,cart-icon-bubble', { method: 'GET' }).then((r) => r.json());
          cartDrawer.renderContents({ sections: data });
        }
      } catch (e) {
        console.error('Failed to add exclusive item', e);
      } finally {
        btn.textContent = 'ADDED ✓';
        btn.disabled = false;
        setCartDrawerLoading(false);
        setTimeout(() => {
          btn.textContent = originalText;
        }, 1500);
      }
    });
  });
})();
